import { useState, useEffect } from 'react'

import MaintenancePage from '../pages/maintenance'

const BYPASS_TOKEN_KEY = 'maintenance_bypass_token'

function getBypassToken() {
  // Check URL first
  const urlParams = new URLSearchParams(window.location.search)
  const urlToken = urlParams.get('bypass')
  if (urlToken) {
    // Store it for future requests
    sessionStorage.setItem(BYPASS_TOKEN_KEY, urlToken)
    return urlToken
  }
  // Check session storage
  return sessionStorage.getItem(BYPASS_TOKEN_KEY)
}

export function useMaintenance() {
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isEnabled: false,
    message: null,
    estimatedEndTime: null,
    loading: true,
    bypassed: false,
  })

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const bypassToken = getBypassToken()
        const apiHost = process.env.REACT_APP_API_HOST || ''

        // Check maintenance status
        const res = await fetch(`${apiHost}/maintenance/status`)
        const data = await res.json()

        if (!data.isEnabled) {
          setMaintenanceStatus({
            isEnabled: false,
            message: null,
            estimatedEndTime: null,
            loading: false,
            bypassed: false,
          })
          return
        }

        // Maintenance is enabled - check bypass token
        if (bypassToken) {
          const verifyRes = await fetch(`${apiHost}/maintenance/verify-bypass?token=${bypassToken}`)
          const verifyData = await verifyRes.json()
          if (verifyData.valid) {
            setMaintenanceStatus({
              isEnabled: true,
              message: data.message,
              estimatedEndTime: data.estimatedEndTime,
              loading: false,
              bypassed: true,
            })
            return
          }
        }

        // No valid bypass - show maintenance page
        setMaintenanceStatus({
          isEnabled: true,
          message: data.message,
          estimatedEndTime: data.estimatedEndTime,
          loading: false,
          bypassed: false,
        })
      } catch (error) {
        console.error('Error checking maintenance status:', error)
        setMaintenanceStatus({
          isEnabled: false,
          message: null,
          estimatedEndTime: null,
          loading: false,
          bypassed: false,
        })
      }
    }

    checkMaintenance()

    // Poll every 30 seconds
    const interval = setInterval(checkMaintenance, 30000)
    return () => clearInterval(interval)
  }, [])

  return maintenanceStatus
}

export function MaintenanceGuard({ children }) {
  const { isEnabled, message, estimatedEndTime, loading, bypassed } = useMaintenance()

  // Show nothing while loading
  if (loading) {
    return null
  }

  // If maintenance is enabled and not bypassed, show maintenance page
  if (isEnabled && !bypassed) {
    return <MaintenancePage message={message} estimatedEndTime={estimatedEndTime} />
  }

  // Otherwise render children
  return children
}
